import { css } from 'aphrodite';
import { useMemo } from 'react';
import {
	useGetBoard,
	useOpportunityReportingChartsQuery,
	useOpportunityReportingSummaryQuery,
} from '../../../../../../queries';
import { OPPORTUNITY_REPORTING_CHARTS } from '../../../../../../queries/Boards/opportunityReportingChartDefinitions';
import { DataDisplay } from '../../../components/DataDisplay';
import { formatCurrency } from '../../../helpers/formatCurrency';
import { styleSheet as advancedReportingStyleSheet } from '../../../styles';
import {
	mapApiRowsToCloseMonth,
	mapApiRowsToDealSizeBuckets,
	mapApiRowsToDealSizeByStage,
	mapApiRowsToOwnerOrCompany,
	mapApiRowsToTag,
} from '../../helpers/mapOpportunityReportingApiToCharts';
import { styleSheet } from '../../styles';
import { useOpportunityReportingFilterBar } from '../../hooks/useOpportunityReportingFilterBar';
import { CloseDateByMonthChart } from '../CloseDateByMonthChart';
import { CreationDateByMonthChart } from '../CreationDateByMonthChart';
import { DealSizeByOwnerChart } from '../DealSizeByOwnerChart';
import { DealSizeByStageChart } from '../DealSizeByStageChart';
import { DealSizeDistributionChart } from '../DealSizeDistributionChart';
import { FilterBar } from '../FilterBar';
import { OpportunitiesByTagChart } from '../OpportunitiesByTagChart';
import { OpportunityCountByStageChart } from '../OpportunityCountByStageChart';
import { TopCompaniesChart } from '../TopCompaniesChart';

interface OverviewTabProps {
	boardId: string | null;
}

export const OverviewTab = ({ boardId }: OverviewTabProps) => {
	const filterBar = useOpportunityReportingFilterBar({ boardId });

	const getBoardQuery = useGetBoard({ boardId: boardId ?? '' });
	const stages = getBoardQuery.data?.stages;
	const isBoardInvalid = !!boardId && getBoardQuery.isFetched && getBoardQuery.isError;
	const boardReportingEnabled = Boolean(boardId) && getBoardQuery.isSuccess;
	const reportingEnabled = boardReportingEnabled;

	const summaryQuery = useOpportunityReportingSummaryQuery({
		boardId: boardId ?? '',
		filterRequest: filterBar.apiFilterRequest,
		enabled: reportingEnabled,
	});

	const chartsQuery = useOpportunityReportingChartsQuery({
		boardId: boardId ?? '',
		filterRequest: filterBar.apiFilterRequest,
		charts: OPPORTUNITY_REPORTING_CHARTS,
		enabled: reportingEnabled,
	});

	const chartsData = chartsQuery.data;
	const showStageCharts = !filterBar.filterStageId;
	const showOwnerChart = filterBar.ownerScope === 'any';

	const headline = useMemo(
		() => ({
			count: summaryQuery.data?.totalOpportunities ?? 0,
			totalDealSize: summaryQuery.data?.totalDealSize ?? 0,
			averageDealSize: summaryQuery.data?.averageDealSize ?? 0,
		}),
		[summaryQuery.data]
	);

	const dealByStage = useMemo(() => mapApiRowsToDealSizeByStage(chartsData?.byStage ?? []), [chartsData?.byStage]);
	const closeByMonth = useMemo(() => mapApiRowsToCloseMonth(chartsData?.byCloseDate ?? []), [chartsData?.byCloseDate]);
	const creationByMonth = useMemo(
		() => mapApiRowsToCloseMonth(chartsData?.byCreationDate ?? []),
		[chartsData?.byCreationDate]
	);
	const byTag = useMemo(() => mapApiRowsToTag(chartsData?.byTag ?? []), [chartsData?.byTag]);
	const dealSizeBuckets = useMemo(
		() => mapApiRowsToDealSizeBuckets(chartsData?.dealSizeDistribution ?? []),
		[chartsData?.dealSizeDistribution]
	);
	const byOwner = useMemo(() => mapApiRowsToOwnerOrCompany(chartsData?.byOwner ?? []), [chartsData?.byOwner]);
	const topCompanies = useMemo(
		() => mapApiRowsToOwnerOrCompany(chartsData?.topCompanies ?? []),
		[chartsData?.topCompanies]
	);

	const isReportingLoading = reportingEnabled && (summaryQuery.isPending || chartsQuery.isPending);

	const reportingLoadError =
		reportingEnabled && (summaryQuery.isError || chartsQuery.isError)
			? (summaryQuery.error ?? chartsQuery.error)
			: undefined;

	return (
		<>
			{stages?.length ? <FilterBar stages={stages} isBoardInvalid={isBoardInvalid} filterBar={filterBar} /> : null}
			{reportingLoadError != null ? (
				<p className={css(styleSheet.errorNote)} role='alert'>
					We couldn&apos;t load this report. Please try again.
				</p>
			) : null}
			{reportingEnabled && !isBoardInvalid ? (
				<div id='exportable-content'>
					<div className={css(advancedReportingStyleSheet.container)}>
						<DataDisplay
							title='Opportunities'
							data={headline.count}
							className={css(advancedReportingStyleSheet.smallDataDisplay)}
							loading={isReportingLoading}
						/>
						<DataDisplay
							title='Total deal size'
							className={css(advancedReportingStyleSheet.smallDataDisplay)}
							data={formatCurrency(headline.totalDealSize)}
							loading={isReportingLoading}
						/>
						<DataDisplay
							title='Average deal size'
							data={formatCurrency(headline.averageDealSize)}
							loading={isReportingLoading}
							className={css(advancedReportingStyleSheet.smallDataDisplay)}
						/>
					</div>

					{showStageCharts ? (
						<div className={css(advancedReportingStyleSheet.chartsSection)}>
							<DealSizeByStageChart data={dealByStage} isLoading={isReportingLoading} />
							<OpportunityCountByStageChart data={dealByStage} isLoading={isReportingLoading} />
						</div>
					) : null}
					<div className={css(advancedReportingStyleSheet.chartsSection)}>
						<CloseDateByMonthChart data={closeByMonth} isLoading={isReportingLoading} />
						<CreationDateByMonthChart data={creationByMonth} isLoading={isReportingLoading} />
					</div>
					<div className={css(advancedReportingStyleSheet.chartsSection)}>
						<OpportunitiesByTagChart data={byTag} isLoading={isReportingLoading} />
						<DealSizeDistributionChart data={dealSizeBuckets} isLoading={isReportingLoading} />
					</div>
					<div className={css(advancedReportingStyleSheet.chartsSection)}>
						{showOwnerChart ? <DealSizeByOwnerChart data={byOwner} isLoading={isReportingLoading} /> : null}
						<TopCompaniesChart
							data={topCompanies}
							isLoading={isReportingLoading}
							className={showOwnerChart ? undefined : css(styleSheet.chartGridFullWidth)}
						/>
					</div>
				</div>
			) : null}
		</>
	);
};
